import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle, ClipboardList, FileText, Download } from 'lucide-react';
import type { IPolicyFieldValue } from '@/@types/insurance';
import { generateCertificatePdf } from '@/lib/certificates/generateCertificate';

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  active: 'default',
  expired: 'outline',
  cancelled: 'destructive',
};

export function CompanyPolicyDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const policyId = id as Id<'insurancePolicies'>; // AS_JUSTIFIED: one-time Id cast from route param at component top
  const policy = useQuery(api['insurance/policies'].get, id ? { policyId } : 'skip');
  const certificateData = useQuery(
    api.insurance.certificateData.getCertificateData,
    id ? { policyId } : 'skip'
  );
  const isLoading = policy === undefined;

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleDownloadCertificate = useCallback(async () => {
    if (!certificateData) return;
    const blob = await generateCertificatePdf(certificateData);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `certificate-${certificateData.policyholder?.policyNumber ?? 'policy'}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  }, [certificateData]);

  if (isLoading) {
    return (
      <div
        className="min-h-full bg-background-secondary p-8 space-y-4"
        role="status"
        aria-label="Loading policy details"
      >
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-7 w-48" />
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle
            className="h-12 w-12 text-muted-foreground mx-auto mb-4"
            aria-hidden="true"
          />
          <h2 className="text-lg font-semibold text-foreground mb-1">Policy not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This policy may have been removed or you don&apos;t have access.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const fields = policy.fieldValues.filter((f: IPolicyFieldValue) => f.value);

  return (
    <div className="min-h-full bg-background-secondary p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <h1 className="text-xl font-semibold text-foreground">{policy.policyNumber}</h1>
          </div>
          <Badge variant={STATUS_VARIANTS[policy.status] ?? 'secondary'} className="capitalize">
            {policy.status}
          </Badge>
          {policy.insuranceTypeName && (
            <span className="text-sm text-muted-foreground">{policy.insuranceTypeName}</span>
          )}
        </div>
        <Button variant="outline" onClick={handleDownloadCertificate} disabled={!certificateData}>
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Download Certificate
        </Button>
      </div>

      {/* Policy Fields */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            Policy Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">No details available.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {fields.map((field: IPolicyFieldValue, idx: number) => (
                <div key={idx}>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    {field.fieldLabel}
                  </p>
                  <p className="text-sm text-foreground">{field.value}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drivers */}
      {policy.drivers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              Drivers ({policy.drivers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {policy.drivers.map(driver => (
                <div key={driver._id} className="rounded-lg border border-border p-3 space-y-1">
                  <p className="text-sm font-medium text-foreground">{driver.fullName}</p>
                  <p className="text-xs text-muted-foreground">
                    {driver.nationality} &middot; DOB: {driver.birthday}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    License: {driver.licenseIssueCountry}, issued {driver.licenseIssueDate}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
